import { useQuery } from "@apollo/client/react";
import { Alert, Button, Chip, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { DownloadRowsReportDocument } from "../../graphql/types";
import type { DownloadRowsReportQuery, DownloadRowsReportQueryVariables } from "../../graphql/types";

import { format, getMonth, getYear } from "date-fns";
import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import Switch from "../../icons/Switch.svg?react";
import { humanizeNumber } from "../../lib/Util";
import { DataLoader, ErrorContainer, LoadingContainer, SuccessContainer } from "../DataLoader";
import { GqlError } from "../common/GqlError";
import { CheckboxWithLabel } from "../ui/CheckboxWithLabel";
import {
  ChartPatterns,
  CustomLegend,
  CustomTooltip,
  DatasetData,
  LoadingContent,
  buildDataset,
  getRangeDate,
} from "./ChartsCommon";

enum BannerState {
  WARNING = "warning",
  DANGER = "error",
  NONE = "none",
}

const bannerToShow = (dataset: Array<DatasetData>, setBanner: (state: BannerState) => void) => {
  if (dataset.length === 0) return;
  const currentMonth = dataset[dataset.length - 1];
  if (
    !currentMonth ||
    !currentMonth.contracted ||
    currentMonth.production === undefined ||
    currentMonth.proof === undefined ||
    currentMonth.backfill === undefined
  )
    return;

  const currentRowCount = currentMonth.production + currentMonth.proof + currentMonth.backfill;

  if (currentRowCount > currentMonth.contracted) {
    setBanner(BannerState.DANGER);
    return;
  }
  if (currentMonth.forecasted && currentRowCount + currentMonth.forecasted > currentMonth.contracted) {
    setBanner(BannerState.WARNING);
  }
};

const ROW_COUNT_WARNING_MESSAGE = "You’re about to go over the contracted rows for this month";

const ROW_COUNT_DANGER_MESSAGE = "You’ve surpassed the contracted rows for this month";

const ChartByPurpose = ({
  handleOnChange,
  useTextures,
  setUseTextures,
}: {
  handleOnChange: () => void;
  useTextures: boolean;
  setUseTextures: (value: boolean) => void;
}) => {
  const theme = useTheme();
  const { data, loading, error } = useQuery<DownloadRowsReportQuery, DownloadRowsReportQueryVariables>(
    DownloadRowsReportDocument,
    {
      fetchPolicy: "cache-first",
      variables: {
        dimensions: ["purpose"],
        filters: {
          monthsAgo: 12,
        },
      },
    }
  );

  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  const { dataset, banner } = useMemo(() => {
    if (!data) return { dataset: [], banner: BannerState.NONE };
    let currentMonthRowCount = 0;
    const newDataset = buildDataset(data.downloadRowsReport?.contracted);
    data.downloadRowsReport?.rows?.forEach((row) => {
      let datasetKey = "production";
      if (row?.purpose == "backfill") {
        datasetKey = "backfill";
      } else if (row?.purpose == "proof") {
        datasetKey = "proof";
      }
      const existingData = newDataset.find(
        (d) => d.month === format(new Date(row?.year, row?.month - 1), "MMM") && d.year === row?.year
      );
      if (existingData) {
        existingData[datasetKey] = row?.totalRowCount;
        if (row.year === getYear(new Date()) && row?.month === getMonth(new Date()) + 1) {
          currentMonthRowCount += row?.totalRowCount;
        }
      }
    });
    if (newDataset.length > 0 && data?.downloadRowsReport?.forecast) {
      newDataset[newDataset.length - 1].forecasted = data?.downloadRowsReport?.forecast - currentMonthRowCount;
    }
    let newBanner = BannerState.NONE;
    bannerToShow(newDataset, (state) => {
      newBanner = state;
    });
    return { dataset: newDataset, banner: newBanner };
  }, [data]);

  const { startingDay, endingDay } = getRangeDate();

  return (
    <DataLoader loading={loading} error={error} success={Boolean(data)}>
      <LoadingContainer>
        <LoadingContent>
          <CircularProgress size="24px" />
        </LoadingContent>
      </LoadingContainer>
      <ErrorContainer>
        <GqlError error={error} />
      </ErrorContainer>
      <SuccessContainer>
        {data ? (
          <>
            <Stack direction="row" sx={{ alignItems: "center", gap: "16px" }}>
              <Typography variant="heading.four">Rows per process</Typography>
              <Button
                variant="text"
                color="secondary"
                size="small"
                startIcon={<Switch />}
                onClick={() => handleOnChange()}
              >
                Change
              </Button>
              <Stack direction="row" sx={{ width: "300px", ml: "auto", justifyContent: "flex-end" }}>
                <CheckboxWithLabel
                  label="Use textures"
                  checked={useTextures}
                  onClick={() => setUseTextures(!useTextures)}
                />
              </Stack>
            </Stack>
            <Stack sx={{ mt: 2 }}>
              <Chip variant="outline" label={`${startingDay} - ${endingDay}`} />
            </Stack>
            {banner !== BannerState.NONE && !isBannerDismissed && (
              <Stack
                sx={{ mt: 2, width: "100%", justifyContent: "center", alignItems: "center", position: "relative" }}
              >
                <Alert
                  severity={banner === BannerState.WARNING ? "warning" : "error"}
                  onClose={() => setIsBannerDismissed(true)}
                  sx={{ position: "absolute", top: "0px" }}
                >
                  {banner === BannerState.WARNING ? ROW_COUNT_WARNING_MESSAGE : ROW_COUNT_DANGER_MESSAGE}
                </Alert>
              </Stack>
            )}
            <Stack sx={{ mt: "66px", width: "100%", height: "480px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={dataset}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                  }}
                  barSize={36}
                >
                  {ChartPatterns}
                  <CartesianGrid strokeLinecap="round" vertical={false} />
                  <YAxis tickFormatter={humanizeNumber} />
                  <XAxis dataKey="label" />
                  <ChartTooltip formatter={(value) => humanizeNumber(value as number)} content={<CustomTooltip />} />
                  <Legend content={<CustomLegend />} />
                  <Bar
                    dataKey="proof"
                    stackId="total"
                    fill={useTextures ? "url(#texturedLime)" : theme.palette.dataLime.main}
                    name="Test"
                  />
                  <Bar
                    dataKey="backfill"
                    name="Backfill"
                    stackId="total"
                    fill={useTextures ? "url(#texturedSky)" : theme.palette.dataSky.main}
                  />
                  <Bar
                    dataKey="production"
                    name="Production"
                    stackId="total"
                    fill={useTextures ? "url(#texturedPeach" : theme.palette.dataPeach.main}
                  />
                  <Bar dataKey="forecasted" name="Forecasted" stackId="total" fill="url(#forecasted)" />
                  <Line
                    type="linear"
                    name="Contracted"
                    dataKey="contracted"
                    stroke={theme.palette.dataCerulean.main}
                    dot={false}
                    strokeWidth={4}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Stack>
          </>
        ) : null}
      </SuccessContainer>
    </DataLoader>
  );
};

export { ChartByPurpose };
